// Q4uDoc canonical specialty vocab — the source of truth in @q4udoc/types
//
// Merged from:
//   - apps/backend/scripts/seedSpecialties.ts (structure baseline: 33 specialties)
//   - question_gen_gem/tag_questions.py SPECIALTY_MAP (added 8 subspecialties)
//
// Structure rule: separate ABMS residencies stay as their own top-level specialty
// (Cardiothoracic Surgery, Neurosurgery, Urology, etc. are NOT nested under a
// "Surgery" parent — this matches how learners identify professionally).
//
// Naming rule: shortest sensible label. When backend and python differed, backend
// won (e.g., "Electrophysiology" not "Cardiac Electrophysiology"). Python entries
// carry a `// +py` comment on the line they were absorbed onto.

import { z } from 'zod';

export const SpecialtySchema = z.object({
  name: z.string(),
  subspecialties: z.array(z.string()),
});

export type Specialty = z.infer<typeof SpecialtySchema>;

export const SPECIALTIES: Specialty[] = [
  // ==================== MEDICAL SPECIALTIES ====================
  {
    name: 'Anesthesiology',
    subspecialties: [
      'General Anesthesiology',
      'Critical Care Medicine',
      'Pain Medicine',
      'Pediatric Anesthesiology',
      'Cardiac Anesthesiology',
      'Obstetric Anesthesiology',
      'Regional Anesthesiology',
    ],
  },
  {
    name: 'Cardiology',
    subspecialties: [
      'General Cardiology',
      'Interventional Cardiology',
      'Electrophysiology',
      'Heart Failure & Transplant',
      'Echocardiography',
      'Nuclear Cardiology',
      'Advanced Cardiac Imaging', // +py (Cardiac CT & MRI)
      'Structural Heart Disease Interventions', // +py
      'Preventive Cardiology',
      'Cardiometabolic Medicine', // +py
      'Cardio-Oncology', // +py
      'Adult Congenital Heart Disease',
    ],
  },
  {
    name: 'Dermatology',
    subspecialties: [
      'General Dermatology',
      'Mohs Surgery',
      'Dermatopathology',
      'Pediatric Dermatology',
      'Cosmetic Dermatology',
    ],
  },
  {
    name: 'Emergency Medicine',
    subspecialties: [
      'General Emergency Medicine',
      'Pediatric Emergency Medicine',
      'Toxicology',
      'Critical Care Medicine', // +py (EM->CCM fellowship path)
      'Ultrasound',
      'Sports Medicine',
      'EMS/Prehospital Medicine',
    ],
  },
  {
    name: 'Endocrinology',
    subspecialties: [
      'General Endocrinology',
      'Diabetes & Metabolism',
      'Thyroid Disorders',
      'Adrenal Disorders',
      'Pituitary & Hypothalamic Disorders',
      'Reproductive Endocrinology',
      'Bone & Mineral Metabolism',
    ],
  },
  {
    name: 'Family Medicine',
    subspecialties: [
      'General Family Medicine',
      'Sports Medicine',
      'Geriatrics',
      'Hospice & Palliative Medicine',
      'Addiction Medicine',
    ],
  },
  {
    name: 'Gastroenterology',
    subspecialties: [
      'General Gastroenterology',
      'Hepatology',
      'Inflammatory Bowel Disease',
      'Interventional Endoscopy',
      'Motility & Functional GI Disorders',
      'Pancreatic & Biliary Disease',
    ],
  },
  {
    name: 'Hematology',
    subspecialties: [
      'General Hematology',
      'Benign Hematology',
      'Coagulation & Thrombosis',
      'Transfusion Medicine',
      'Bone Marrow Failure Syndromes',
    ],
  },
  {
    name: 'Infectious Disease',
    subspecialties: [
      'General Infectious Disease',
      'HIV Medicine',
      'Tropical & Travel Medicine',
      'Hospital Epidemiology & Infection Control',
      'Antimicrobial Stewardship',
      'Transplant Infectious Disease',
      'Fungal & Parasitic Infections', // +py (content bucket, not a fellowship)
    ],
  },
  {
    name: 'Internal Medicine',
    subspecialties: [
      'General Internal Medicine',
      'Hospital Medicine',
      'Geriatrics',
      'Palliative Care',
    ],
  },
  {
    name: 'Medical Genetics',
    subspecialties: ['Clinical Genetics', 'Biochemical Genetics', 'Molecular Genetics'],
  },
  {
    name: 'Nephrology',
    subspecialties: [
      'General Nephrology',
      'Glomerular Disease',
      'Dialysis & Renal Replacement Therapy',
      'Transplant Nephrology',
      'Fluid, Electrolytes & Acid-Base',
      'Hypertension & Renovascular Disease',
    ],
  },
  {
    name: 'Neurology',
    subspecialties: [
      'General Neurology',
      'Stroke & Vascular Neurology',
      'Epilepsy',
      'Neuromuscular Medicine',
      'Movement Disorders',
      'Neurointensive Care',
      'Headache Medicine',
      'Neuro-Oncology',
      'Multiple Sclerosis & Neuroimmunology',
    ],
  },
  {
    name: 'Obstetrics & Gynecology',
    subspecialties: [
      'General Obstetrics',
      'General Gynecology',
      'Maternal-Fetal Medicine',
      'Gynecologic Oncology',
      'Urogynecology',
      'Reproductive Endocrinology & Infertility',
      'Minimally Invasive Gynecologic Surgery',
    ],
  },
  {
    name: 'Oncology',
    subspecialties: [
      'Medical Oncology',
      'Hematology-Oncology',
      'Breast Oncology',
      'GI Oncology',
      'Thoracic Oncology',
      'GU Oncology',
    ],
  },
  {
    name: 'Ophthalmology',
    subspecialties: [
      'General Ophthalmology',
      'Retina & Vitreous',
      'Glaucoma',
      'Cornea & External Disease',
      'Pediatric Ophthalmology',
      'Neuro-Ophthalmology',
      'Oculoplastics',
    ],
  },
  {
    name: 'Pathology',
    subspecialties: [
      'General Pathology', // +py (AP+CP combined content)
      'Anatomic Pathology',
      'Clinical Pathology',
      'Hematopathology',
      'Neuropathology',
      'Dermatopathology',
      'Cytopathology',
      'Forensic Pathology',
      'Surgical Pathology',
    ],
  },
  {
    name: 'Pediatrics',
    subspecialties: [
      'General Pediatrics',
      'Neonatology',
      'Pediatric Critical Care',
      'Pediatric Cardiology',
      'Pediatric Hematology-Oncology',
      'Pediatric Gastroenterology',
      'Pediatric Pulmonology',
      'Pediatric Nephrology',
      'Pediatric Infectious Disease',
      'Developmental-Behavioral Pediatrics',
    ],
  },
  {
    name: 'Physical Medicine & Rehabilitation',
    subspecialties: [
      'General PM&R',
      'Spinal Cord Injury Medicine',
      'Brain Injury Medicine',
      'Sports Medicine',
      'Pain Medicine',
      'Pediatric Rehabilitation',
    ],
  },
  {
    name: 'Preventive Medicine',
    subspecialties: [
      'General Preventive Medicine',
      'Occupational Medicine',
      'Aerospace Medicine',
      'Public Health',
    ],
  },
  {
    name: 'Psychiatry',
    subspecialties: [
      'General Psychiatry',
      'Child and Adolescent Psychiatry',
      'Addiction Psychiatry',
      'Forensic Psychiatry',
      'Geriatric Psychiatry',
      'Consultation-Liaison Psychiatry',
    ],
  },
  {
    name: 'Pulmonology',
    subspecialties: [
      'General Pulmonology',
      'Critical Care Medicine',
      'Interventional Pulmonology',
      'Sleep Medicine',
      'Pulmonary Hypertension',
      'Interstitial Lung Disease',
      'Obstructive Lung Disease', // +py (COPD/asthma content bucket)
    ],
  },
  {
    name: 'Radiation Oncology',
    subspecialties: [
      'General Radiation Oncology',
      'Brachytherapy',
      'Stereotactic Radiosurgery',
      'Pediatric Radiation Oncology',
    ],
  },
  {
    name: 'Radiology',
    subspecialties: [
      'Diagnostic Radiology',
      'Interventional Radiology',
      'Neuroradiology',
      'Nuclear Medicine',
      'Pediatric Radiology',
      'Musculoskeletal Radiology',
      'Breast Imaging',
      'Abdominal Radiology',
    ],
  },
  {
    name: 'Rheumatology',
    subspecialties: [
      'General Rheumatology',
      'Inflammatory Arthritis',
      'Connective Tissue Diseases',
      'Vasculitis',
      'Crystal Arthropathies',
      'Osteoporosis & Metabolic Bone Disease',
    ],
  },

  // ==================== SURGICAL SPECIALTIES (Independent Residencies) ====================
  {
    name: 'General Surgery',
    subspecialties: [
      'Acute Care Surgery',
      'Trauma Surgery',
      'Surgical Critical Care',
      'Minimally Invasive Surgery',
      'Bariatric Surgery',
      'Surgical Oncology',
      'Colorectal Surgery',
      'Breast Surgery',
      'Hepatobiliary Surgery',
    ],
  },
  {
    name: 'Cardiothoracic Surgery',
    subspecialties: [
      'Adult Cardiac Surgery',
      'Congenital Cardiac Surgery',
      'Thoracic Surgery',
      'Heart & Lung Transplant',
    ],
  },
  {
    name: 'Neurosurgery',
    subspecialties: [
      'General Neurosurgery',
      'Spine Surgery',
      'Neuro-Oncology',
      'Cerebrovascular Surgery',
      'Functional Neurosurgery',
      'Pediatric Neurosurgery',
      'Skull Base Surgery',
    ],
  },
  {
    name: 'Orthopedic Surgery',
    subspecialties: [
      'General Orthopedics',
      'Sports Medicine',
      'Hand Surgery',
      'Spine Surgery',
      'Joint Replacement & Reconstruction',
      'Trauma',
      'Pediatric Orthopedics',
      'Foot & Ankle',
      'Shoulder & Elbow',
      'Orthopedic Oncology',
    ],
  },
  {
    name: 'Otolaryngology',
    subspecialties: [
      'General Otolaryngology',
      'Head & Neck Surgery',
      'Rhinology & Skull Base Surgery',
      'Otology & Neurotology',
      'Laryngology',
      'Pediatric Otolaryngology',
      'Facial Plastic Surgery',
    ],
  },
  {
    name: 'Plastic Surgery',
    subspecialties: [
      'General Plastic Surgery',
      'Hand Surgery',
      'Craniofacial Surgery',
      'Microsurgery & Reconstruction',
      'Burn Surgery',
      'Aesthetic Surgery',
    ],
  },
  {
    name: 'Urology',
    subspecialties: [
      'General Urology',
      'Urologic Oncology',
      'Female Pelvic Medicine',
      'Male Infertility & Andrology',
      'Pediatric Urology',
      'Endourology & Stone Disease',
      'Neurourology',
    ],
  },
  {
    name: 'Vascular Surgery',
    subspecialties: [
      'General Vascular Surgery',
      'Endovascular Surgery',
      'Aortic Surgery',
      'Venous Disease',
    ],
  },

  // ==================== DENTAL PROFESSIONS ====================
  //
  // Separate profession from medicine (DDS/DMD, not MD/DO), grouped here so
  // taxonomy consumers can filter out or exclusively include as needed.
  {
    name: 'Dentistry',
    subspecialties: [
      'General Dentistry',
      'Endodontics',
      'Oral & Maxillofacial Surgery',
      'Oral & Maxillofacial Pathology',
      'Oral & Maxillofacial Radiology',
      'Orthodontics & Dentofacial Orthopedics',
      'Pediatric Dentistry',
      'Periodontics',
      'Prosthodontics',
      'Dental Public Health',
      'Oral Medicine',
      'Dental Anesthesiology',
    ],
  },

  // ==================== BASIC SCIENCES ====================
  {
    name: 'Basic Medical Sciences',
    subspecialties: [
      'Anatomy',
      'Physiology',
      'Biochemistry',
      'Pharmacology',
      'Microbiology & Immunology',
      'Genetics & Genomics',
      'Embryology & Development',
      'Histology & Cell Biology',
      'Neuroscience', // organ-system slice among cross-cutting disciplines — kept because USMLE Step 1 and NBME subject exams treat it as a distinct category
      'Pathophysiology',
    ],
  },
];

// Denormalized lookup: SPECIALTY_MAP[specialty] = [sub1, sub2, ...]
export const SPECIALTY_MAP: Record<string, readonly string[]> = Object.freeze(
  Object.fromEntries(SPECIALTIES.map((s) => [s.name, Object.freeze([...s.subspecialties])])),
);

// Flat list of top-level specialty names
export const SPECIALTY_NAMES: readonly string[] = Object.freeze(SPECIALTIES.map((s) => s.name));

// Specialties that accept interns (prelim/categorical PGY-1 positions). A curated
// subset of SPECIALTIES the mobile app uses to gate the intern specialty picker.
// It lives here (not hand-typed in the app) so it stays locked to the canonical
// names: every entry MUST be an exact SPECIALTY_NAMES value. The Vocab drift check
// (scripts/check_intern_specialties.mjs) fails CI if any entry drifts — the two
// mobile copies previously drifted ('Obstetrics and Gynecology'/'Neurological
// Surgery' never matched the served 'Obstetrics & Gynecology'/'Neurosurgery', so
// interns silently lost those specialties from the picker).
export const INTERN_ALLOWED_SPECIALTIES: readonly string[] = Object.freeze([
  'Internal Medicine',
  'Pediatrics',
  'Family Medicine',
  'Emergency Medicine',
  'Obstetrics & Gynecology',
  'Psychiatry',
  'General Surgery',
  'Pathology',
  'Neurosurgery',
  'Orthopedic Surgery',
]);

// Type guard for validation at API boundaries
export type SpecialtyName = (typeof SPECIALTIES)[number]['name'];
